import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import AppLayout from '../../components/app-layout/AppLayout'
import CardSurface from '../../components/card-surface/CardSurface'
import TenancySwitcher from '../../components/tenancy-switcher/TenancySwitcher'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import {
  cancelMyAmenityBooking,
  getBuildingAmenities,
  getMyAmenityBookings,
} from '../../_data/resident'
import { ResidentAmenity, ResidentAmenityBooking } from '../../_types/resident'
import {
  formatAmenityDays,
  formatAmenityDeposit,
  formatAmenityHours,
  formatBookingShape,
  formatBookingStatus,
  formatBookingWhen,
  getBookingStatusTone,
} from './helpers'
import {
  PageStack,
  PageHeader,
  PageTitle,
  PageSubtitle,
  QuietText,
  AmenityList,
  AmenityName,
  AmenityDescription,
  AmenityMetaRow,
  MetaChip,
  DepositTag,
  ApprovalNote,
  SubHeading,
  BookButton,
  BookingRow,
  BookingInfo,
  BookingName,
  BookingWhen,
  BookingActions,
  StatusTag,
  CancelLink,
} from './styled'

const isBookingCancellable = (booking: ResidentAmenityBooking): boolean => {
  const isActive =
    booking.status === 'confirmed' ||
    booking.status === 'pending-approval' ||
    booking.status === 'pending-payment'
  return isActive === true && booking.startTime > Date.now()
}

/**
 * The renter's Amenities surface for the selected residence: their upcoming
 * bookings (with cancel) on top, then the bookable catalogue. Both free and paid
 * amenities open a slot picker (/amenities/:id); paid ones place a refundable
 * deposit hold via Stripe Checkout. Keyed on the selected tenancy.
 *
 * @returns {JSX.Element}
 */
const AmenitiesPage = (): JSX.Element => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { selectedTenancy, selectedTenancyId, isLoadingTenancies } =
    useResidentTenancy()
  const [amenities, setAmenities] = useState<ResidentAmenity[]>([])
  const [bookings, setBookings] = useState<ResidentAmenityBooking[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [cancellingId, setCancellingId] = useState<string>('')

  useEffect(() => {
    if (selectedTenancyId === '') {
      setAmenities([])
      setBookings([])
      setIsLoading(false)
      return
    }

    let isMounted = true
    setAmenities([])
    setBookings([])
    setIsLoading(true)

    void Promise.all([
      getBuildingAmenities(selectedTenancyId),
      getMyAmenityBookings(selectedTenancyId),
    ]).then(([loadedAmenities, loadedBookings]) => {
      if (isMounted === false) {
        return
      }
      setAmenities(loadedAmenities)
      setBookings(loadedBookings)
      setIsLoading(false)
    })

    return () => {
      isMounted = false
    }
  }, [selectedTenancyId])

  const handleCancel = (bookingId: string): void => {
    setCancellingId(bookingId)
    void cancelMyAmenityBooking(bookingId).then(wasCancelled => {
      setCancellingId('')
      if (wasCancelled === false) {
        return
      }
      setBookings(previous =>
        previous.map(booking =>
          booking.id === bookingId
            ? { ...booking, status: 'cancelled' }
            : booking,
        ),
      )
    })
  }

  const hasAmenities = amenities.length > 0
  const hasBookings = bookings.length > 0
  const isResolving = isLoadingTenancies === true || isLoading === true
  const justPaidDeposit = searchParams.get('deposit') === 'success'

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack>
        <PageHeader>
          <PageTitle>Amenities</PageTitle>
          <PageSubtitle>
            Book the shared amenities at your building.
          </PageSubtitle>
        </PageHeader>

        <TenancySwitcher />

        {justPaidDeposit === true ? (
          <CardSurface padding="standard">
            <QuietText>
              Your deposit hold was placed — your booking will confirm here in a
              moment.
            </QuietText>
          </CardSurface>
        ) : null}

        {isResolving === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading…</QuietText>
          </CardSurface>
        ) : null}

        {isResolving === false && selectedTenancy === null ? (
          <CardSurface padding="loose">
            <QuietText>
              You don&rsquo;t have any tenancies yet. When your property manager
              invites you, claim the invite from your email to see your
              building&rsquo;s amenities here.
            </QuietText>
          </CardSurface>
        ) : null}

        {isResolving === false &&
        selectedTenancy !== null &&
        hasBookings === true ? (
          <>
            <SubHeading>Your bookings</SubHeading>
            <AmenityList>
              {bookings.map(booking => (
                <CardSurface key={booking.id} padding="standard">
                  <BookingRow>
                    <BookingInfo>
                      <BookingName>{booking.amenityName}</BookingName>
                      <BookingWhen>{formatBookingWhen(booking)}</BookingWhen>
                    </BookingInfo>
                    <BookingActions>
                      <StatusTag tone={getBookingStatusTone(booking.status)}>
                        {formatBookingStatus(booking.status)}
                      </StatusTag>
                      {isBookingCancellable(booking) === true ? (
                        <CancelLink
                          type="button"
                          disabled={cancellingId === booking.id}
                          onClick={() => handleCancel(booking.id)}
                        >
                          Cancel
                        </CancelLink>
                      ) : null}
                    </BookingActions>
                  </BookingRow>
                </CardSurface>
              ))}
            </AmenityList>
          </>
        ) : null}

        {isResolving === false &&
        selectedTenancy !== null &&
        hasAmenities === false ? (
          <CardSurface padding="loose">
            <QuietText>
              No amenities listed yet. When your property manager adds one, it
              shows up here.
            </QuietText>
          </CardSurface>
        ) : null}

        {isResolving === false && hasAmenities === true ? (
          <>
            <SubHeading>Available to book</SubHeading>
            <AmenityList>
              {amenities.map(amenity => (
                <CardSurface key={amenity.id} padding="standard">
                  <AmenityName>{amenity.name}</AmenityName>
                  {amenity.description !== '' ? (
                    <AmenityDescription>
                      {amenity.description}
                    </AmenityDescription>
                  ) : null}
                  <AmenityMetaRow>
                    <MetaChip>
                      {formatBookingShape(amenity.bookingShape)}
                    </MetaChip>
                    <MetaChip>{formatAmenityHours(amenity)}</MetaChip>
                    <MetaChip>{formatAmenityDays(amenity.daysOfWeek)}</MetaChip>
                    <MetaChip>
                      {amenity.capacity === 1
                        ? '1 spot'
                        : `${amenity.capacity} spots`}
                    </MetaChip>
                    <DepositTag
                      tone={amenity.isPaid === true ? 'paid' : 'free'}
                    >
                      {formatAmenityDeposit(amenity)}
                    </DepositTag>
                  </AmenityMetaRow>
                  {amenity.requiresApproval === true ? (
                    <ApprovalNote>
                      Bookings need manager approval before they&rsquo;re
                      confirmed.
                    </ApprovalNote>
                  ) : null}
                  <BookButton
                    type="button"
                    onClick={() => navigate(`/amenities/${amenity.id}`)}
                  >
                    {amenity.isPaid === true
                      ? 'Reserve with deposit'
                      : 'Book a time'}
                  </BookButton>
                </CardSurface>
              ))}
            </AmenityList>
          </>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default AmenitiesPage
